// THE LIVING MATERIALS' SURFACES (the owner, Sep 30: a review of every material's look, life's 127 materials included,
// which until now were flat colour). One generator per kind of tissue: bark, grain, leaves, petals, moss, gills, threads,
// polyps, branches, blades, cells, slime, lichen, vines, scales, spores, fibres, seed heads, tar and plates.
//
// The same four rules as texture-materials.js, for the same reasons (its header says why):
//   * 1024 x 1024, and it TILES: every feature placed at a hashed position is drawn again across any edge it reaches
//     (drawWrapped), and every band or line that runs off one side comes back on the other.
//   * DETERMINISTIC: all jitter is hash01 off a seed taken from the hex, never Math.random().
//   * NEUTRAL: drawn in greys on a light ground, because the material's colour multiplies it on the GPU (applyMaterial
//     sets material.color to the hex), and one generator serves every material of a family.
//   * CHEAP: paths and gradients, no per-pixel loops.
import * as THREE from '/lib/three/three.module.min.js';   // REAL path, not the bare specifier: must resolve on Safari 15.6
import { hash01, hexSeed } from './texture-helpers.js';
import { drawWrapped } from './texture-materials.js';

const SIZE = 1024;
function canvas(ground) {
    const c = document.createElement('canvas');
    c.width = c.height = SIZE;
    const ctx = c.getContext('2d');
    ctx.fillStyle = ground; ctx.fillRect(0, 0, SIZE, SIZE);
    return [c, ctx];
}
function finish(c) {
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return t;
}
const grey = (v, a = 1) => { const g = Math.max(0, Math.min(255, Math.round(v))); return `rgba(${g},${g},${g},${a})`; };
// A smooth closed wobble around (x, y): radius r, n lobes, amplitude k of r. For petals, cells, polyps and lichen.
function blob(ctx, x, y, r, k, n, ph) {
    ctx.beginPath();
    for (let i = 0; i <= 48; i++) {
        const a = i / 48 * Math.PI * 2, rr = r * (1 + k * Math.sin(a * n + ph) + 0.5 * k * Math.sin(a * (n + 3) + ph * 1.7));
        const px = x + Math.cos(a) * rr, py = y + Math.sin(a) * rr;
        if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py);
    }
    ctx.closePath();
}
// A vertical wavy line from top to bottom that comes back where it left: its phase is whole cycles per tile.
function waveV(ctx, x, amp, cycles, ph, step = 16) {
    ctx.beginPath();
    for (let y = -step; y <= SIZE + step; y += step) {
        const px = x + amp * Math.sin((y / SIZE) * cycles * Math.PI * 2 + ph) + 0.4 * amp * Math.sin((y / SIZE) * (cycles * 2 + 1) * Math.PI * 2 + ph * 2);
        if (y === -step) ctx.moveTo(px, y); else ctx.lineTo(px, y);
    }
}

// ── WOOD ────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Furrowed bark: deep vertical fissures between raised plates, the plates cross-cracked.
export function createBarkFurrows(hex) {
    const [c, ctx] = canvas('#b4b4b4'), s = hexSeed(hex);
    for (let i = 0; i < 26; i++) {
        const x = (i + hash01(s, i) * 0.6) * SIZE / 26, amp = 10 + hash01(s + 1, i) * 18, cyc = 1 + Math.floor(hash01(s + 2, i) * 3), ph = hash01(s + 3, i) * 6.28;
        for (const ox of [-SIZE, 0, SIZE]) {
            ctx.save(); ctx.translate(ox, 0);
            waveV(ctx, x, amp, cyc, ph); ctx.strokeStyle = grey(60, 0.85); ctx.lineWidth = 9 + hash01(s + 4, i) * 10; ctx.stroke();
            waveV(ctx, x + 6, amp, cyc, ph); ctx.strokeStyle = grey(225, 0.35); ctx.lineWidth = 3; ctx.stroke();
            ctx.restore();
        }
    }
    for (let i = 0; i < 160; i++) {                       // cross-cracks across the plates
        const x = hash01(s + 5, i) * SIZE, y = hash01(s + 6, i) * SIZE, w = 14 + hash01(s + 7, i) * 26;
        drawWrapped(SIZE, x, y, w, (px, py) => { ctx.strokeStyle = grey(85, 0.55); ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(px - w / 2, py); ctx.lineTo(px + w / 2, py + (hash01(s + 8, i) - 0.5) * 8); ctx.stroke(); });
    }
    return finish(c);
}
// Sawn grain: long flowing growth lines, knots that the lines bend round.
export function createWoodGrain(hex) {
    const [c, ctx] = canvas('#c2c2c2'), s = hexSeed(hex);
    const knots = Array.from({ length: 5 }, (_, k) => [hash01(s, k + 50) * SIZE, hash01(s, k + 60) * SIZE, 30 + hash01(s, k + 70) * 40]);
    for (let i = 0; i < 70; i++) {
        const x0 = i * SIZE / 70, dark = hash01(s + 9, i) < 0.5;
        ctx.beginPath();
        for (let y = -16; y <= SIZE + 16; y += 8) {
            let x = x0 + 14 * Math.sin(y / SIZE * Math.PI * 2 * 2 + i * 0.3);
            for (const [kx, ky, kr] of knots) {
                for (const ox of [-SIZE, 0, SIZE]) for (const oy of [-SIZE, 0, SIZE]) {
                    const dx = x - (kx + ox), dy = y - (ky + oy), d2 = dx * dx + dy * dy;
                    x += dx * (kr * kr * 1.6) / (d2 + kr * kr) * (d2 < 40000 ? 1 : 0) * 0.5;
                }
            }
            if (y === -16) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = grey(dark ? 95 : 150, dark ? 0.55 : 0.4); ctx.lineWidth = dark ? 3 : 1.6; ctx.stroke();
    }
    for (const [kx, ky, kr] of knots) drawWrapped(SIZE, kx, ky, kr, (px, py) => {
        for (let r = kr; r > 3; r -= 6) { ctx.beginPath(); ctx.ellipse(px, py, r * 0.55, r, 0, 0, Math.PI * 2); ctx.strokeStyle = grey(80, 0.5); ctx.lineWidth = 2.5; ctx.stroke(); }
        ctx.beginPath(); ctx.ellipse(px, py, kr * 0.2, kr * 0.35, 0, 0, Math.PI * 2); ctx.fillStyle = grey(55, 0.8); ctx.fill();
    });
    return finish(c);
}
// End grain: growth rings round several centres, split by radial checks.
export function createGrowthRings(hex) {
    const [c, ctx] = canvas('#bdbdbd'), s = hexSeed(hex);
    for (let k = 0; k < 4; k++) {
        const cx = hash01(s, k + 10) * SIZE, cy = hash01(s, k + 20) * SIZE, R = 220 + hash01(s, k + 30) * 160;
        drawWrapped(SIZE, cx, cy, R, (px, py) => {
            ctx.save(); ctx.beginPath(); ctx.arc(px, py, R, 0, Math.PI * 2); ctx.clip();
            ctx.fillStyle = grey(196); ctx.fill();
            for (let r = R; r > 4; r -= 7 + hash01(s + k, r) * 9) { blob(ctx, px, py, r, 0.03, 5, k); ctx.strokeStyle = grey(110, 0.45 + hash01(s, r) * 0.3); ctx.lineWidth = 2; ctx.stroke(); }
            for (let j = 0; j < 7; j++) { const a = hash01(s + k, j + 40) * 6.28; ctx.beginPath(); ctx.moveTo(px + Math.cos(a) * R * 0.25, py + Math.sin(a) * R * 0.25); ctx.lineTo(px + Math.cos(a) * R, py + Math.sin(a) * R); ctx.strokeStyle = grey(60, 0.6); ctx.lineWidth = 3; ctx.stroke(); }
            ctx.beginPath(); ctx.arc(px, py, R, 0, Math.PI * 2); ctx.strokeStyle = grey(70, 0.9); ctx.lineWidth = 14; ctx.stroke();
            ctx.restore();
        });
    }
    return finish(c);
}

// ── LEAVES, PETALS, BLADES ──────────────────────────────────────────────────────────────────────────────────────────
// Overlapping leaves, each with a midrib and side veins, lit along one edge.
export function createLeafVeins(hex) {
    const [c, ctx] = canvas('#8e8e8e'), s = hexSeed(hex);
    for (let i = 0; i < 90; i++) {
        const x = hash01(s, i) * SIZE, y = hash01(s + 1, i) * SIZE, L = 70 + hash01(s + 2, i) * 70, a = hash01(s + 3, i) * 6.28, g = 160 + hash01(s + 4, i) * 70;
        drawWrapped(SIZE, x, y, L, (px, py) => {
            ctx.save(); ctx.translate(px, py); ctx.rotate(a);
            ctx.beginPath(); ctx.moveTo(-L, 0); ctx.quadraticCurveTo(0, -L * 0.42, L, 0); ctx.quadraticCurveTo(0, L * 0.42, -L, 0);
            ctx.fillStyle = grey(g); ctx.fill(); ctx.strokeStyle = grey(70, 0.6); ctx.lineWidth = 2; ctx.stroke();
            ctx.beginPath(); ctx.moveTo(-L, 0); ctx.lineTo(L * 0.95, 0); ctx.strokeStyle = grey(g + 30, 0.9); ctx.lineWidth = 3; ctx.stroke();
            for (let v = -0.7; v <= 0.7; v += 0.22) { ctx.beginPath(); ctx.moveTo(L * v, 0); ctx.lineTo(L * (v + 0.22), -L * 0.26 * (1 - Math.abs(v))); ctx.moveTo(L * v, 0); ctx.lineTo(L * (v + 0.22), L * 0.26 * (1 - Math.abs(v))); ctx.strokeStyle = grey(g - 45, 0.6); ctx.lineWidth = 1.5; ctx.stroke(); }
            ctx.restore();
        });
    }
    return finish(c);
}
// Small leaves in close rows, like a conifer's shoots or a fern's pinnae.
export function createLeafScales(hex) {
    const [c, ctx] = canvas('#7c7c7c'), s = hexSeed(hex);
    const rows = 22, per = 18;
    for (let r = 0; r < rows; r++) for (let k = 0; k < per; k++) {
        const x = (k + (r % 2) * 0.5) * SIZE / per + (hash01(s, r * per + k) - 0.5) * 10, y = r * SIZE / rows, L = 34, g = 150 + hash01(s + 1, r * per + k) * 80;
        drawWrapped(SIZE, x, y, L, (px, py) => {
            ctx.beginPath(); ctx.moveTo(px - L * 0.55, py - L * 0.2); ctx.quadraticCurveTo(px, py + L * 1.3, px + L * 0.55, py - L * 0.2); ctx.quadraticCurveTo(px, py, px - L * 0.55, py - L * 0.2);
            ctx.fillStyle = grey(g); ctx.fill(); ctx.strokeStyle = grey(60, 0.5); ctx.lineWidth = 1.5; ctx.stroke();
            ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, py + L * 0.8); ctx.strokeStyle = grey(g + 25, 0.8); ctx.lineWidth = 1.5; ctx.stroke();
        });
    }
    return finish(c);
}
// Layered rounded petals, the outer ones darker, each with fine radial lines.
export function createPetalLayers(hex) {
    const [c, ctx] = canvas('#a0a0a0'), s = hexSeed(hex);
    for (let i = 0; i < 46; i++) {
        const x = hash01(s, i) * SIZE, y = hash01(s + 1, i) * SIZE, R = 50 + hash01(s + 2, i) * 60, n = 5 + Math.floor(hash01(s + 3, i) * 4), ph = hash01(s + 4, i) * 6.28;
        drawWrapped(SIZE, x, y, R * 1.2, (px, py) => {
            for (let ring = 0; ring < 3; ring++) {
                const rr = R * (1 - ring * 0.3);
                for (let p = 0; p < n; p++) {
                    const a = ph + ring * 0.4 + p / n * Math.PI * 2;
                    ctx.save(); ctx.translate(px + Math.cos(a) * rr * 0.45, py + Math.sin(a) * rr * 0.45); ctx.rotate(a);
                    ctx.beginPath(); ctx.ellipse(0, 0, rr * 0.55, rr * 0.32, 0, 0, Math.PI * 2);
                    ctx.fillStyle = grey(150 + ring * 35); ctx.fill(); ctx.strokeStyle = grey(80, 0.45); ctx.lineWidth = 1.5; ctx.stroke();
                    for (let l = -2; l <= 2; l++) { ctx.beginPath(); ctx.moveTo(-rr * 0.45, l * 3); ctx.lineTo(rr * 0.4, l * 6); ctx.strokeStyle = grey(110 + ring * 30, 0.35); ctx.lineWidth = 1; ctx.stroke(); }
                    ctx.restore();
                }
            }
            ctx.beginPath(); ctx.arc(px, py, R * 0.14, 0, Math.PI * 2); ctx.fillStyle = grey(70); ctx.fill();
        });
    }
    return finish(c);
}
// Long wavy ribbons, kelp or grass blades, each with a pale midline.
export function createKelpBlades(hex) {
    const [c, ctx] = canvas('#6e6e6e'), s = hexSeed(hex);
    for (let i = 0; i < 34; i++) {
        const x = (i + hash01(s, i)) * SIZE / 34, amp = 18 + hash01(s + 1, i) * 26, cyc = 1 + Math.floor(hash01(s + 2, i) * 3), ph = hash01(s + 3, i) * 6.28, w = 18 + hash01(s + 4, i) * 22, g = 140 + hash01(s + 5, i) * 90;
        for (const ox of [-SIZE, 0, SIZE]) {
            ctx.save(); ctx.translate(ox, 0);
            waveV(ctx, x, amp, cyc, ph); ctx.strokeStyle = grey(40, 0.7); ctx.lineWidth = w + 4; ctx.stroke();
            waveV(ctx, x, amp, cyc, ph); ctx.strokeStyle = grey(g); ctx.lineWidth = w; ctx.stroke();
            waveV(ctx, x - w * 0.15, amp, cyc, ph); ctx.strokeStyle = grey(g + 40, 0.6); ctx.lineWidth = 2.5; ctx.stroke();
            ctx.restore();
        }
    }
    return finish(c);
}
// Parallel fibres: reeds, stalks, a stem's vascular strands.
export function createFibrousStrands(hex) {
    const [c, ctx] = canvas('#9a9a9a'), s = hexSeed(hex);
    for (let i = 0; i < 220; i++) {
        const x = hash01(s, i) * SIZE, g = 90 + hash01(s + 1, i) * 140, w = 2 + hash01(s + 2, i) * 6;
        for (const ox of [-SIZE, 0, SIZE]) { ctx.save(); ctx.translate(ox, 0); waveV(ctx, x, 4 + hash01(s + 3, i) * 6, 1, hash01(s + 4, i) * 6.28, 32); ctx.strokeStyle = grey(g, 0.7); ctx.lineWidth = w; ctx.stroke(); ctx.restore(); }
    }
    for (let i = 0; i < 9; i++) { const y = (i + hash01(s, i + 300)) * SIZE / 9; ctx.fillStyle = grey(70, 0.45); ctx.fillRect(0, y, SIZE, 4); ctx.fillStyle = grey(220, 0.3); ctx.fillRect(0, y + 4, SIZE, 2); }   // nodes, as on a reed
    return finish(c);
}

// ── SOFT TISSUE, FUNGI, SLIME ──────────────────────────────────────────────────────────────────────────────────────
// Dense moss: thousands of short tufts, darker in the hollows between clumps.
export function createMossTufts(hex) {
    const [c, ctx] = canvas('#7a7a7a'), s = hexSeed(hex);
    for (let i = 0; i < 70; i++) drawWrapped(SIZE, hash01(s, i) * SIZE, hash01(s + 1, i) * SIZE, 90, (px, py) => {
        const g = ctx.createRadialGradient(px, py, 0, px, py, 90); g.addColorStop(0, grey(190, 0.6)); g.addColorStop(1, grey(190, 0)); ctx.fillStyle = g; ctx.fillRect(px - 90, py - 90, 180, 180); });
    for (let i = 0; i < 9000; i++) {
        const x = hash01(s + 2, i) * SIZE, y = hash01(s + 3, i) * SIZE, a = hash01(s + 4, i) * 6.28, L = 4 + hash01(s + 5, i) * 9;
        drawWrapped(SIZE, x, y, L, (px, py) => { ctx.strokeStyle = grey(120 + hash01(s + 6, i) * 120, 0.8); ctx.lineWidth = 1.6; ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + Math.cos(a) * L, py + Math.sin(a) * L); ctx.stroke(); });
    }
    return finish(c);
}
// Mushroom gills: fans of fine radial lines round each cap's stem.
export function createFungalGills(hex) {
    const [c, ctx] = canvas('#b0b0b0'), s = hexSeed(hex);
    for (let k = 0; k < 9; k++) {
        const x = hash01(s, k) * SIZE, y = hash01(s + 1, k) * SIZE, R = 120 + hash01(s + 2, k) * 110;
        drawWrapped(SIZE, x, y, R, (px, py) => {
            ctx.beginPath(); ctx.arc(px, py, R, 0, Math.PI * 2); ctx.fillStyle = grey(200); ctx.fill();
            const n = Math.floor(R * 0.9);
            for (let j = 0; j < n; j++) { const a = j / n * Math.PI * 2, r0 = (j % 3 === 0) ? R * 0.12 : (j % 3 === 1 ? R * 0.45 : R * 0.7); ctx.beginPath(); ctx.moveTo(px + Math.cos(a) * r0, py + Math.sin(a) * r0); ctx.lineTo(px + Math.cos(a) * R, py + Math.sin(a) * R); ctx.strokeStyle = grey(95, 0.55); ctx.lineWidth = 1.3; ctx.stroke(); }
            ctx.beginPath(); ctx.arc(px, py, R * 0.1, 0, Math.PI * 2); ctx.fillStyle = grey(235); ctx.fill();
            ctx.beginPath(); ctx.arc(px, py, R, 0, Math.PI * 2); ctx.strokeStyle = grey(70, 0.8); ctx.lineWidth = 6; ctx.stroke();
        });
    }
    return finish(c);
}
// Mycelium: a branching web of fine pale threads over darker ground.
export function createMyceliumWeb(hex) {
    const [c, ctx] = canvas('#6a6a6a'), s = hexSeed(hex);
    const grow = (x, y, a, len, depth, id) => {
        if (depth > 5 || len < 6) return;
        const x2 = x + Math.cos(a) * len, y2 = y + Math.sin(a) * len;
        drawWrapped(SIZE, (x + x2) / 2, (y + y2) / 2, len, (px, py) => { const dx = px - (x + x2) / 2, dy = py - (y + y2) / 2; ctx.beginPath(); ctx.moveTo(x + dx, y + dy); ctx.lineTo(x2 + dx, y2 + dy); ctx.strokeStyle = grey(200 + depth * 8, 0.75 - depth * 0.08); ctx.lineWidth = Math.max(0.8, 3.2 - depth * 0.5); ctx.stroke(); });
        grow(x2, y2, a + (hash01(id, depth) - 0.5) * 1.1, len * 0.82, depth + 1, id * 3 + 1);
        if (hash01(id, depth + 9) < 0.6) grow(x2, y2, a + (hash01(id + 5, depth) < 0.5 ? -1 : 1) * (0.5 + hash01(id, depth + 3) * 0.6), len * 0.7, depth + 1, id * 3 + 2);
    };
    for (let i = 0; i < 60; i++) grow(hash01(s, i) * SIZE, hash01(s + 1, i) * SIZE, hash01(s + 2, i) * 6.28, 40 + hash01(s + 3, i) * 40, 0, s * 101 + i);
    return finish(c);
}
// Plant cells: a close mosaic of rounded cells with bright walls, like flesh or pith under a lens.
export function createCellMembrane(hex) {
    const [c, ctx] = canvas('#7e7e7e'), s = hexSeed(hex);
    const cols = 16, rows = 16;
    for (let r = 0; r < rows; r++) for (let k = 0; k < cols; k++) {
        const x = (k + 0.5 + (r % 2) * 0.5 + (hash01(s, r * 31 + k) - 0.5) * 0.35) * SIZE / cols, y = (r + 0.5 + (hash01(s + 1, r * 31 + k) - 0.5) * 0.35) * SIZE / rows, R = SIZE / cols * 0.5;
        drawWrapped(SIZE, x, y, R * 1.2, (px, py) => {
            blob(ctx, px, py, R * 0.98, 0.06, 4, r * 7 + k);
            const g = ctx.createRadialGradient(px - R * 0.3, py - R * 0.3, 2, px, py, R); g.addColorStop(0, grey(225)); g.addColorStop(1, grey(150));
            ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = grey(235, 0.9); ctx.lineWidth = 3; ctx.stroke();
            ctx.beginPath(); ctx.arc(px + (hash01(s + 2, r * 31 + k) - 0.5) * R * 0.6, py + (hash01(s + 3, r * 31 + k) - 0.5) * R * 0.6, R * 0.16, 0, Math.PI * 2); ctx.fillStyle = grey(90, 0.7); ctx.fill();   // the nucleus
        });
    }
    return finish(c);
}
// Slime: glossy blobs merging into one another, each with a highlight.
export function createSlimeGloss(hex) {
    const [c, ctx] = canvas('#8a8a8a'), s = hexSeed(hex);
    for (let i = 0; i < 120; i++) {
        const x = hash01(s, i) * SIZE, y = hash01(s + 1, i) * SIZE, R = 24 + hash01(s + 2, i) * hash01(s + 3, i) * 110;
        drawWrapped(SIZE, x, y, R * 1.3, (px, py) => {
            blob(ctx, px, py, R, 0.12, 3, i);
            const g = ctx.createRadialGradient(px, py, R * 0.1, px, py, R * 1.1); g.addColorStop(0, grey(200, 0.85)); g.addColorStop(1, grey(110, 0.85));
            ctx.fillStyle = g; ctx.fill();
            ctx.beginPath(); ctx.ellipse(px - R * 0.35, py - R * 0.4, R * 0.28, R * 0.14, -0.6, 0, Math.PI * 2); ctx.fillStyle = grey(255, 0.7); ctx.fill();
        });
    }
    return finish(c);
}
// Lichen: rosettes of lobed crust, ringed, on bare ground.
export function createLichenRosettes(hex) {
    const [c, ctx] = canvas('#9c9c9c'), s = hexSeed(hex);
    for (let i = 0; i < 200; i++) { const x = hash01(s + 9, i) * SIZE, y = hash01(s + 10, i) * SIZE; drawWrapped(SIZE, x, y, 6, (px, py) => { ctx.fillStyle = grey(110, 0.35); ctx.fillRect(px, py, 3, 3); }); }
    for (let i = 0; i < 70; i++) {
        const x = hash01(s, i) * SIZE, y = hash01(s + 1, i) * SIZE, R = 30 + hash01(s + 2, i) * 70;
        drawWrapped(SIZE, x, y, R * 1.3, (px, py) => {
            for (let r = R; r > 6; r -= 9) { blob(ctx, px, py, r, 0.12, 9, i + r * 0.01); ctx.fillStyle = grey(160 + (R - r) * 0.8); ctx.fill(); ctx.strokeStyle = grey(85, 0.5); ctx.lineWidth = 2; ctx.stroke(); }
        });
    }
    return finish(c);
}
// Spores and seeds: a field of small domed spheres, packed and shadowed. Lovely when the material glows.
export function createSporeDots(hex) {
    const [c, ctx] = canvas('#5e5e5e'), s = hexSeed(hex);
    for (let i = 0; i < 1400; i++) {
        const x = hash01(s, i) * SIZE, y = hash01(s + 1, i) * SIZE, R = 4 + hash01(s + 2, i) * hash01(s + 3, i) * 22;
        drawWrapped(SIZE, x, y, R + 3, (px, py) => {
            ctx.beginPath(); ctx.arc(px + 2, py + 2, R, 0, Math.PI * 2); ctx.fillStyle = grey(30, 0.5); ctx.fill();
            const g = ctx.createRadialGradient(px - R * 0.35, py - R * 0.35, 0.5, px, py, R); g.addColorStop(0, grey(255)); g.addColorStop(1, grey(150));
            ctx.beginPath(); ctx.arc(px, py, R, 0, Math.PI * 2); ctx.fillStyle = g; ctx.fill();
        });
    }
    return finish(c);
}
// Seed head: a honeycomb of seeds in spiralling rows, as on a sunflower's disc.
export function createSeedHex(hex) {
    const [c, ctx] = canvas('#6c6c6c'), s = hexSeed(hex);
    const n = 24, cell = SIZE / n;
    for (let r = 0; r < n; r++) for (let k = 0; k < n; k++) {
        const x = (k + (r % 2) * 0.5) * cell, y = r * cell * 0.866 * (n / (n * 0.866)), R = cell * 0.48;
        drawWrapped(SIZE, x, y, R, (px, py) => {
            ctx.beginPath(); for (let j = 0; j < 6; j++) { const a = j / 6 * Math.PI * 2 + Math.PI / 6; const qx = px + Math.cos(a) * R, qy = py + Math.sin(a) * R; if (j) ctx.lineTo(qx, qy); else ctx.moveTo(qx, qy); } ctx.closePath();
            const g = ctx.createRadialGradient(px - R * 0.3, py - R * 0.3, 1, px, py, R); g.addColorStop(0, grey(220 - hash01(s, r * 41 + k) * 50)); g.addColorStop(1, grey(120));
            ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = grey(50, 0.8); ctx.lineWidth = 2; ctx.stroke();
        });
    }
    return finish(c);
}

// ── CORAL, SHELL, SCALE ─────────────────────────────────────────────────────────────────────────────────────────────
// Polyps: cup-shaped openings packed over a coral head, each a ring with a dark centre.
export function createCoralPolyps(hex) {
    const [c, ctx] = canvas('#a8a8a8'), s = hexSeed(hex);
    for (let i = 0; i < 520; i++) {
        const x = hash01(s, i) * SIZE, y = hash01(s + 1, i) * SIZE, R = 10 + hash01(s + 2, i) * 18;
        drawWrapped(SIZE, x, y, R + 4, (px, py) => {
            ctx.beginPath(); ctx.arc(px, py, R, 0, Math.PI * 2); ctx.fillStyle = grey(215); ctx.fill(); ctx.strokeStyle = grey(110, 0.6); ctx.lineWidth = 2; ctx.stroke();
            for (let j = 0; j < 8; j++) { const a = j / 8 * Math.PI * 2; ctx.beginPath(); ctx.moveTo(px + Math.cos(a) * R * 0.4, py + Math.sin(a) * R * 0.4); ctx.lineTo(px + Math.cos(a) * R * 0.9, py + Math.sin(a) * R * 0.9); ctx.strokeStyle = grey(140, 0.6); ctx.lineWidth = 1.2; ctx.stroke(); }
            ctx.beginPath(); ctx.arc(px, py, R * 0.35, 0, Math.PI * 2); ctx.fillStyle = grey(60, 0.9); ctx.fill();
        });
    }
    return finish(c);
}
// Branching coral: forked rounded branches, lit along their tops.
export function createCoralBranches(hex) {
    const [c, ctx] = canvas('#727272'), s = hexSeed(hex);
    const limb = (x, y, a, len, w, depth, id) => {
        if (depth > 4) return;
        const x2 = x + Math.cos(a) * len, y2 = y + Math.sin(a) * len, mx = (x + x2) / 2, my = (y + y2) / 2;
        drawWrapped(SIZE, mx, my, len + w, (px, py) => { const dx = px - mx, dy = py - my;
            ctx.lineCap = 'round'; ctx.beginPath(); ctx.moveTo(x + dx, y + dy); ctx.lineTo(x2 + dx, y2 + dy); ctx.strokeStyle = grey(70); ctx.lineWidth = w + 4; ctx.stroke();
            ctx.strokeStyle = grey(190); ctx.lineWidth = w; ctx.stroke();
            ctx.beginPath(); ctx.moveTo(x + dx - w * 0.2, y + dy - w * 0.2); ctx.lineTo(x2 + dx - w * 0.2, y2 + dy - w * 0.2); ctx.strokeStyle = grey(240, 0.6); ctx.lineWidth = w * 0.3; ctx.stroke(); });
        limb(x2, y2, a - 0.45 - hash01(id, depth) * 0.3, len * 0.78, w * 0.75, depth + 1, id * 2 + 1);
        limb(x2, y2, a + 0.45 + hash01(id + 3, depth) * 0.3, len * 0.78, w * 0.75, depth + 1, id * 2 + 2);
    };
    for (let i = 0; i < 16; i++) limb(hash01(s, i) * SIZE, hash01(s + 1, i) * SIZE, hash01(s + 2, i) * 6.28, 70 + hash01(s + 3, i) * 30, 22, 0, s * 37 + i);
    return finish(c);
}
// Scales, overlapping in offset rows, each shaded from its free edge.
export function createScaleArmor(hex) {
    const [c, ctx] = canvas('#5a5a5a'), s = hexSeed(hex);
    const n = 16, w = SIZE / n, h = w * 0.62;
    for (let r = -1; r <= Math.ceil(SIZE / h) + 1; r++) for (let k = -1; k <= n; k++) {
        const x = (k + (r % 2 ? 0.5 : 0)) * w, y = r * h;
        for (const [ox, oy] of [[0, 0], [SIZE, 0]]) {
            const px = x + ox, py = y + oy;
            ctx.beginPath(); ctx.moveTo(px - w / 2, py); ctx.quadraticCurveTo(px - w / 2, py + h * 1.6, px, py + h * 1.6); ctx.quadraticCurveTo(px + w / 2, py + h * 1.6, px + w / 2, py); ctx.closePath();
            const g = ctx.createLinearGradient(px, py, px, py + h * 1.6); g.addColorStop(0, grey(120)); g.addColorStop(1, grey(220 - hash01(s, r * 50 + k) * 40));
            ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = grey(40, 0.8); ctx.lineWidth = 2; ctx.stroke();
        }
    }
    return finish(c);
}
// Shell growth lines: concentric arcs as on a clam or calcite crust.
export function createShellGrowth(hex) {
    const [c, ctx] = canvas('#c6c6c6'), s = hexSeed(hex);
    for (let k = 0; k < 6; k++) {
        const x = hash01(s, k) * SIZE, y = hash01(s + 1, k) * SIZE, R = 170 + hash01(s + 2, k) * 120, a0 = hash01(s + 3, k) * 6.28;
        drawWrapped(SIZE, x, y, R, (px, py) => {
            for (let r = R; r > 10; r -= 5 + hash01(s + k, r) * 7) { ctx.beginPath(); ctx.arc(px, py, r, a0, a0 + Math.PI * 1.15); ctx.strokeStyle = grey(hash01(s, r + k) < 0.3 ? 90 : 150, 0.6); ctx.lineWidth = hash01(s + 1, r) < 0.2 ? 3 : 1.5; ctx.stroke(); }
            for (let j = 0; j < 12; j++) { const a = a0 + j / 11 * Math.PI * 1.15; ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + Math.cos(a) * R, py + Math.sin(a) * R); ctx.strokeStyle = grey(110, 0.35); ctx.lineWidth = 2; ctx.stroke(); }
        });
    }
    return finish(c);
}

// ── VINES, VEINS, TAR ───────────────────────────────────────────────────────────────────────────────────────────────
// A tangle of vines: long curving stems in two depths, with leaves budding along them.
export function createVineTangle(hex) {
    const [c, ctx] = canvas('#6a6a6a'), s = hexSeed(hex);
    for (let i = 0; i < 40; i++) {
        const y0 = hash01(s, i) * SIZE, amp = 40 + hash01(s + 1, i) * 80, cyc = 1 + Math.floor(hash01(s + 2, i) * 3), ph = hash01(s + 3, i) * 6.28, front = i % 3 !== 0;
        for (const oy of [-SIZE, 0, SIZE]) {
            ctx.beginPath();
            for (let x = -16; x <= SIZE + 16; x += 12) { const y = oy + y0 + amp * Math.sin(x / SIZE * cyc * Math.PI * 2 + ph); if (x === -16) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
            ctx.strokeStyle = grey(front ? 60 : 50, 0.8); ctx.lineWidth = front ? 9 : 6; ctx.stroke();
            ctx.strokeStyle = grey(front ? 185 : 120); ctx.lineWidth = front ? 5 : 3; ctx.stroke();
            if (front) for (let x = 20; x < SIZE; x += 70 + Math.floor(hash01(s + i, x) * 60)) {
                const y = oy + y0 + amp * Math.sin(x / SIZE * cyc * Math.PI * 2 + ph), up = hash01(s + i, x + 7) < 0.5 ? -1 : 1;
                ctx.save(); ctx.translate(x, y); ctx.rotate(up * 0.8); ctx.beginPath(); ctx.ellipse(0, up * 14, 8, 15, 0, 0, Math.PI * 2); ctx.fillStyle = grey(205); ctx.fill(); ctx.strokeStyle = grey(70, 0.6); ctx.lineWidth = 1.5; ctx.stroke(); ctx.restore();
            }
        }
    }
    return finish(c);
}
// Tar flow: slow swirled bands, darker in the troughs, as tholin or resin sets.
export function createTarSwirl(hex) {
    const [c, ctx] = canvas('#8c8c8c'), s = hexSeed(hex);
    for (let i = 0; i < 60; i++) {
        const y0 = i * SIZE / 60, ph = hash01(s, i) * 6.28;
        for (const oy of [-SIZE, 0, SIZE]) {
            ctx.beginPath();
            for (let x = -16; x <= SIZE + 16; x += 8) { const y = oy + y0 + 28 * Math.sin(x / SIZE * Math.PI * 4 + ph * 0.2 + y0 * 0.01) + 14 * Math.sin(x / SIZE * Math.PI * 6 + i); if (x === -16) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
            ctx.strokeStyle = grey(i % 3 === 0 ? 50 : (i % 3 === 1 ? 130 : 190), 0.55); ctx.lineWidth = 6 + hash01(s + 1, i) * 10; ctx.stroke();
        }
    }
    for (let i = 0; i < 90; i++) drawWrapped(SIZE, hash01(s + 2, i) * SIZE, hash01(s + 3, i) * SIZE, 12, (px, py) => { ctx.beginPath(); ctx.ellipse(px, py, 9, 5, 0.3, 0, Math.PI * 2); ctx.fillStyle = grey(240, 0.55); ctx.fill(); });   // gloss beads
    return finish(c);
}

// Every organic style, as TextureEngine's TEXTURE_STYLES lists the rest: family 'life'.
export const ORGANIC_STYLES = [
    ['barkfurrows',   'Furrowed bark',     createBarkFurrows],
    ['woodgrain',     'Sawn grain',        createWoodGrain],
    ['growthrings',   'Growth rings',      createGrowthRings],
    ['leafveins',     'Veined leaves',     createLeafVeins],
    ['leafscales',    'Leaf scales',       createLeafScales],
    ['petallayers',   'Layered petals',    createPetalLayers],
    ['kelpblades',    'Kelp blades',       createKelpBlades],
    ['fibres',        'Fibrous strands',   createFibrousStrands],
    ['moss',          'Moss tufts',        createMossTufts],
    ['gills',         'Mushroom gills',    createFungalGills],
    ['mycelium',      'Mycelium web',      createMyceliumWeb],
    ['cells',         'Plant cells',       createCellMembrane],
    ['slime',         'Slime gloss',       createSlimeGloss],
    ['lichen',        'Lichen rosettes',   createLichenRosettes],
    ['spores',        'Spore beads',       createSporeDots],
    ['seedhex',       'Seed head',         createSeedHex],
    ['polyps',        'Coral polyps',      createCoralPolyps],
    ['coralbranch',   'Branching coral',   createCoralBranches],
    ['scales',        'Overlapping scales',createScaleArmor],
    ['shell',         'Shell growth',      createShellGrowth],
    ['vines',         'Vine tangle',       createVineTangle],
    ['tarswirl',      'Tar swirl',         createTarSwirl],
];
